import type {
    WebsocketConnection,
    WebsocketConnectionFactory,
    WebsocketConnectionOptions,
    WebsocketDynamicArgumentsResult
} from '../common/ws/types.ts';

export type WebSocketHeaderOptions = {
    headers?: Record<string, string>;
    getHeaders?: () => Record<string, string> | Promise<Record<string, string>>;
    getWebSocketProtocols?: (headers?: Readonly<Record<string, string>>, protocols?: string | readonly string[]) => string | string[] | undefined | {
        protocols?: string | string[],
        headers?: Record<string, string>
    };
    getWebSocketSearchParams?: (headers?: Readonly<Record<string, string>>, search?: string) => Record<string, string | undefined> | undefined;
};

type ProtocolsAsOptions = {
    protocols?: string | string[],
    headers?: Record<string, string>
}

const defaultWebSocketProtocols: WebSocketHeaderOptions['getWebSocketProtocols'] = (
    headers?: Readonly<Record<string, string>>, protocols?: string | readonly string[] | Readonly<ProtocolsAsOptions>): string | string[] | undefined | ProtocolsAsOptions => {
    // protocols can already be wrapped in object (with headers), or just an array/string
    protocols = typeof protocols === 'string' ? [protocols] : protocols;

    if (typeof protocols === 'object') {
        if (Array.isArray(protocols)) {
            if (headers !== undefined) {
                return {
                    headers: { ...headers },
                    protocols: [...protocols],
                };
            }
            return [...protocols];
        }
        return {
            ...protocols,
            headers: { ...protocols['headers'], ...headers }
        };
    }
    else if (protocols === undefined) {
        if (headers !== undefined) {
            return { headers: { ...headers } };
        }
    }
};

class WebsocketConnectionFactoryWrapper<T> implements WebsocketConnectionFactory<T> {
    readonly #factory: WebsocketConnectionFactory<T>;
    readonly #opts: WebSocketHeaderOptions;

    constructor(factory: WebsocketConnectionFactory<T>, opts: WebSocketHeaderOptions) {
        this.#factory = factory;
        this.#opts = opts;
    }

    create(uri: string, protocols?: string | string [], opts?: WebsocketConnectionOptions): WebsocketConnection<T> {
        return this.#factory.create(uri, protocols, this.#wrapWebsocketConnectionOptions(opts));
    }

    close() {
        this.#factory.close();
    }

    #wrapWebsocketConnectionOptions(opts?: WebsocketConnectionOptions) {
        const wrappedOpts = { ...opts };
        const dynamicArguments = opts?.dynamicArguments;
        const URLCtor = opts?.URL ?? (globalThis.URL ?? URL)
        wrappedOpts.dynamicArguments = this.#dynamicArguments(URLCtor, dynamicArguments);
        return wrappedOpts;
    }

    #dynamicArguments(URLCtor: typeof URL,
                      dynamicArguments?: (url: string | URL, protocols?: string | readonly string[]) => Promise<WebsocketDynamicArgumentsResult> | WebsocketDynamicArgumentsResult) {

        return async (url: string | URL, protocols?: string | readonly string[]) => {
            const result = (await dynamicArguments?.(url, protocols)) ?? {};
            const headers = { ...this.#opts.headers };
            const dynamicHeaders = this.#opts.getHeaders ? await this.#opts.getHeaders() : {};
            for (const [key, value] of Object.entries(dynamicHeaders)) {
                headers[key] = value;
            }
            url = new URLCtor(result.url ?? url, result.baseUrl);

            const searchParams = this.#opts.getWebSocketSearchParams?.(headers, url.search);
            if (searchParams) {
                for (const [key, value] of Object.entries(searchParams)) {
                    if (value === undefined) {
                        url.searchParams.delete(key);
                    }
                    else {
                        url.searchParams.set(key, value);
                    }
                }
            }
            result.url = url;

            // user provided getWebSocketProtocols takes precedence over default
            // only use default if getWebSocketSearchParams is not defined
            const getProtocols = (this.#opts.getWebSocketProtocols ?? this.#opts.getWebSocketSearchParams === undefined ? defaultWebSocketProtocols : undefined);
            // cast is needed because getWebSocketProtocols is allowed to return both string[] and {protocols, headers}
            // {protocols, headers} case is not supported in browser environment, but most other WebSocket libraries support it
            result.protocols = getProtocols?.(headers, result.protocols ?? protocols) as WebsocketDynamicArgumentsResult['protocols'];
            return result;
        };
    }
}

export const wrapWithHeaders = <T>(factory: WebsocketConnectionFactory<T>, opts?: WebSocketHeaderOptions): WebsocketConnectionFactory<T> => {
    if (opts) {
        if (opts.getWebSocketProtocols || opts.getWebSocketSearchParams || opts.getHeaders || opts.headers) {
            return new WebsocketConnectionFactoryWrapper(factory, opts)
        }
    }
    return factory;

}
